import type { Auth } from './type'
import { getPublicAuth } from './util'
import { AUTH, CARTO_CLIENT_ID } from '@/config/settings'

const sessionKeyStore = new Map<string, string | null>()

const AUTH0_TOKEN_KEY_SUFFIX = String(AUTH.authorizationParams?.audience ?? '')
const AUTH0_USER_KEY_SUFFIX = '@@user@@'

function getAuth0Key(suffix: string) {
  const AUTH0_STORAGE_KEY_PREFIX = `@@auth0spajs@@::${CARTO_CLIENT_ID}::${suffix}`
  if (!sessionKeyStore.has(suffix)) {
    const data = Object.keys(localStorage).find((key) =>
      key.startsWith(AUTH0_STORAGE_KEY_PREFIX),
    )

    if (!data) return null

    sessionKeyStore.set(suffix, data)
  }

  return sessionKeyStore.get(suffix) ?? null
}

export function getAccessToken(): Auth | null {
  const isAppPublic = getPublicAuth()

  if (isAppPublic) return isAppPublic

  const auth0Key = getAuth0Key(AUTH0_TOKEN_KEY_SUFFIX)
  if (!auth0Key) return null

  const response = localStorage.getItem(auth0Key)
  if (!response) return null

  try {
    const parse = JSON.parse(response) as {
      body: {
        access_token: string
      }
    }

    return parse?.body?.access_token || null
  } catch (error) {
    console.error('Error parsing auth token:', error)
    return null
  }
}

export function saveAccessToken(auth: Auth) {
  const isAppPublic = getPublicAuth()
  if (isAppPublic) return

  const auth0Key = getAuth0Key(AUTH0_TOKEN_KEY_SUFFIX)
  if (!auth0Key) return

  const currentAuth = localStorage.getItem(auth0Key)
  if (currentAuth) {
    try {
      const parse = JSON.parse(currentAuth) as {
        body: {
          access_token: string
        }
      }

      parse.body.access_token = auth

      localStorage.setItem(auth0Key, JSON.stringify(parse))
    } catch (error) {
      console.error('Error parsing auth token:', error)
    }
  }
}

export function clearAccessToken() {
  const isAppPublic = getPublicAuth()
  if (isAppPublic) return

  const auth0Key = getAuth0Key(AUTH0_TOKEN_KEY_SUFFIX)
  if (!auth0Key) return

  localStorage.removeItem(auth0Key)
  sessionKeyStore.clear()
}

export function getUserId(): Auth | null {
  const isAppPublic = getPublicAuth()

  if (isAppPublic) return null

  const auth0Key = getAuth0Key(AUTH0_USER_KEY_SUFFIX)
  if (!auth0Key) return null

  const response = localStorage.getItem(auth0Key)
  if (!response) return null

  try {
    const parse = JSON.parse(response) as {
      decodedToken: {
        user: {
          sub: string
        }
      }
    }

    return parse?.decodedToken?.user?.sub || null
  } catch (error) {
    console.error('Error parsing auth info:', error)
    return null
  }
}

export function getUserEmail(): Auth | null {
  const isAppPublic = getPublicAuth()

  if (isAppPublic) return null

  const auth0Key = getAuth0Key(AUTH0_USER_KEY_SUFFIX)
  if (!auth0Key) return null

  const response = localStorage.getItem(auth0Key)
  if (!response) return null

  try {
    const parse = JSON.parse(response) as {
      decodedToken: {
        user: {
          email: string
        }
      }
    }

    return parse?.decodedToken?.user?.email || null
  } catch (error) {
    console.error('Error parsing auth info:', error)
    return null
  }
}
